import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { openNotification } from 'components/notification';
import { ApiDocServiceCustomFieldsRepository } from 'services/custom-fields/ApiDocServiceCustomFieldsRepository';
import type { AssignedCustomField } from 'services/custom-fields/schemas';
import { QueryKeys } from 'utils/queryKeys';
import { MutationKeys } from 'utils/mutationKeys';

const docServiceCustomFieldsRepository = new ApiDocServiceCustomFieldsRepository();

export type UnassignCustomFieldMutation = ReturnType<typeof useUnassignCustomFieldMutation>;

export function useUnassignCustomFieldMutation(contentId: string, level: 'document' | 'template') {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const assignedKey = QueryKeys.editorAssignedCustomFields(contentId);

  return useMutation({
    mutationKey: [...MutationKeys.unassignCustomField, contentId],

    onMutate: async (customFieldId: string) => {
      await queryClient.cancelQueries({ queryKey: assignedKey });

      const previous = queryClient.getQueryData<AssignedCustomField[]>(assignedKey);
      const removedField = previous?.find((f) => f.id === customFieldId);

      queryClient.setQueryData<AssignedCustomField[]>(assignedKey, (old = []) => old.filter((f) => f.id !== customFieldId));

      return { previous, removedField };
    },

    mutationFn: async (customFieldId: string) => {
      await docServiceCustomFieldsRepository.unassignCustomField(contentId, customFieldId, level);
    },

    onError: (_error, _customFieldId, ctx) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(assignedKey, ctx.previous);
      }
      openNotification({
        type: 'error',
        title: t('editor.variables.unassign_custom_field_error'),
      });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.customFields });
      openNotification({
        type: 'success',
        title: t('editor.variables.unassign_custom_field_success'),
      });
    },
  });
}
